// Helpers for the Financial Reconciliation page. All dates are Indian (IST)
// calendar days in YYYY-MM-DD, the same days the server aggregates by.

const istDateFormatter = new Intl.DateTimeFormat("en-CA", {
  timeZone: "Asia/Kolkata",
  year: "numeric",
  month: "2-digit",
  day: "2-digit",
});

export const istToday = () => istDateFormatter.format(new Date());

export const shiftDay = (day, delta) => {
  const d = new Date(`${day}T00:00:00Z`);
  d.setUTCDate(d.getUTCDate() + delta);
  return d.toISOString().slice(0, 10);
};

export const daysBetween = (from, to) =>
  Math.round((new Date(`${to}T00:00:00Z`) - new Date(`${from}T00:00:00Z`)) / 86400000) + 1;

export const MAX_RANGE_DAYS = 92;

export const RANGE_PRESETS = [
  { id: "today", label: "Today", range: () => ({ from: istToday(), to: istToday() }) },
  { id: "yesterday", label: "Yesterday", range: () => ({ from: shiftDay(istToday(), -1), to: shiftDay(istToday(), -1) }) },
  { id: "7d", label: "Last 7 days", range: () => ({ from: shiftDay(istToday(), -6), to: istToday() }) },
  { id: "30d", label: "Last 30 days", range: () => ({ from: shiftDay(istToday(), -29), to: istToday() }) },
  { id: "90d", label: "Last 90 days", range: () => ({ from: shiftDay(istToday(), -89), to: istToday() }) },
];

const dayLabelFormatter = new Intl.DateTimeFormat("en-IN", { day: "2-digit", month: "short", year: "numeric", timeZone: "UTC" });
const weekdayFormatter = new Intl.DateTimeFormat("en-IN", { weekday: "short", timeZone: "UTC" });

export const formatDay = (day) => (day ? dayLabelFormatter.format(new Date(`${day}T00:00:00Z`)) : "—");
export const formatWeekday = (day) => (day ? weekdayFormatter.format(new Date(`${day}T00:00:00Z`)) : "");

// "2026-10-30T14:05:09" (already IST) -> "30 Oct 2026, 2:05 pm"
export const formatISTTimestamp = (ts) => {
  if (!ts) return "—";
  const [date, time = ""] = ts.split("T");
  const [h = "0", m = "00"] = time.split(":");
  const hour = Number(h);
  const suffix = hour >= 12 ? "pm" : "am";
  return `${formatDay(date)}, ${hour % 12 || 12}:${m} ${suffix}`;
};

export const RECON_STATUS = {
  reconciled: { label: "Reconciled", className: "border-green-200 bg-green-50 text-green-700", dot: "bg-green-500" },
  pending: { label: "Pending", className: "border-amber-200 bg-amber-50 text-amber-800", dot: "bg-amber-500" },
  mismatch: { label: "Mismatch", className: "border-red-200 bg-red-50 text-red-700", dot: "bg-red-500" },
  insufficient_data: { label: "Insufficient data", className: "border-neutral-200 bg-neutral-100 text-neutral-600", dot: "bg-neutral-400" },
};

export const ISSUE_LABELS = {
  not_credited: "Received, not credited",
  over_credited: "Credited more than received",
  partially_credited: "Credited less than received",
  refund_not_recorded: "Refund missing in CMS",
};

export const PURPOSE_LABELS = {
  wallet_topup: "Wallet top-up",
  qr_topup: "QR payment",
  subscription: "Subscription",
  unknown: "Not linked to CMS",
};

// change vs the previous period as a fraction (0.12 = +12%), for DeltaBadge;
// undefined = no comparison available, null = previous period was zero
export const relativeChange = (current, previous) => {
  if (previous === null || previous === undefined || current === null || current === undefined) return undefined;
  if (previous === 0) return current === 0 ? 0 : null;
  return (current - previous) / Math.abs(previous);
};

const csvCell = (value) => {
  if (value === null || value === undefined) return "";
  const s = String(value);
  return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
};

// rows: array of arrays; first row is the header
export const downloadCSV = (filename, rows) => {
  const csv = rows.map((r) => r.map(csvCell).join(",")).join("\n");
  const blob = new Blob([`﻿${csv}`], { type: "text/csv;charset=utf-8" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
};

// plain 2-decimal amount for CSV files (no currency symbol or grouping)
export const csvAmount = (n) => (n === null || n === undefined ? "" : Number(n).toFixed(2));
